/**
 * Utility functions for transforming and working with order data
 */

import type {
  CustomProduct,
  OrderWithCustomProducts,
  FormattedOrdersResponse,
  PageInfo,
  SearchOrderQueryVariables,
  GetQueryVariablesParams,
} from './types';

/**
 * Default page size for order queries
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Type guard to check if an order product is valid
 */
const isValidOrderProduct = (
  product: any
): product is { product: any; amount: number } => {
  return (
    product &&
    typeof product === 'object' &&
    'product' in product &&
    'amount' in product
  );
};

/**
 * Extract product information from an order product
 * Combines the product details with the amount from the order
 *
 * @param orderProduct - The order product fragment from GraphQL
 * @returns CustomProduct with flattened structure
 */
export const extractProductInfo = (orderProduct: any): CustomProduct => {
  const product = orderProduct.product || {};
  return {
    id: product.id || '',
    externalId: product.externalId ?? null,
    unit: product.unit ?? null,
    name: product.name || '',
    amount: orderProduct.amount ?? 0,
  };
};

/**
 * Format a single order by transforming orderProducts array to products array
 * This flattens the nested structure for easier consumption in the UI
 *
 * @param orderData - Raw order data from GraphQL query
 * @returns Order with custom products structure
 */
export const formatOrder = (orderData: any): OrderWithCustomProducts => {
  const { orderProducts, ...rest } = orderData;

  const products = (orderProducts ?? [])
    .filter(isValidOrderProduct)
    .map(extractProductInfo);

  return {
    ...rest,
    products,
  };
};

/**
 * Type guard to check if the search order response is a valid connection
 */
const isSearchOrderConnection = (
  data: any
): data is {
  __typename: 'SearchOrderConnection';
  edges: Array<{ node: any; cursor: string }>;
  pageInfo: PageInfo;
  total?: number | null;
} => {
  return data?.__typename === 'SearchOrderConnection';
};

/**
 * Format the orders search response from GraphQL
 * Extracts orders from connection edges and returns formatted data
 *
 * @param response - Raw response from GetOrders query
 * @returns Formatted orders with page info
 */
export const formatOrdersResponse = (
  response: any | undefined
): FormattedOrdersResponse => {
  const searchOrder = response?.searchOrder;

  if (!isSearchOrderConnection(searchOrder)) {
    return {
      orders: [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null,
      },
    };
  }

  const formattedOrders = (searchOrder.edges || []).map(({ node }) =>
    formatOrder(node)
  );

  return {
    orders: formattedOrders,
    pageInfo: searchOrder.pageInfo,
  };
};

/**
 * Build query variables for order search
 * Handles cursor-based pagination and search parameters
 *
 * @param params - Search parameters and pagination cursors
 * @returns GraphQL query variables
 */
export const getQueryVariables = ({
  searchTerm = '',
  supplierRefId = '',
  pageSize = DEFAULT_PAGE_SIZE,
  cursorBefore,
  cursorAfter,
}: GetQueryVariablesParams = {}): SearchOrderQueryVariables => {
  return {
    query: {
      keywords: searchTerm.length > 0 ? searchTerm : undefined,
      supplierRefId: supplierRefId.length > 0 ? supplierRefId : undefined,
    },
    after: cursorAfter,
    before: cursorBefore,
    first: pageSize,
  };
};

/**
 * Check if an order is a test order
 *
 * @param order - The order to check
 * @returns True if the order is marked as a test order
 */
export const isTestOrder = (order: OrderWithCustomProducts): boolean => {
  return order.isTest === true;
};

/**
 * Format order reference ID for display
 *
 * @param order - The order to format
 * @returns Formatted reference ID or fallback
 */
export const formatOrderReferenceId = (
  order: OrderWithCustomProducts
): string => {
  return order.referenceId || order.id;
};

/**
 * Get order display name (buyer display name or name)
 *
 * @param order - The order to get buyer name from
 * @returns Buyer display name or name
 */
export const getOrderBuyerDisplayName = (
  order: OrderWithCustomProducts
): string => {
  if (!order.buyer) return 'Unknown Buyer';
  return order.buyer.displayName || order.buyer.name || 'Unknown Buyer';
};

/**
 * Get order supplier name
 *
 * @param order - The order to get supplier name from
 * @returns Distributor name
 */
export const getOrderSupplierName = (
  order: OrderWithCustomProducts
): string => {
  return order.supplier?.name || 'Unknown Distributor';
};

/**
 * Format delivery date for display
 *
 * @param order - The order to format date from
 * @returns Formatted delivery date or fallback
 */
export const formatDeliveryDate = (order: OrderWithCustomProducts): string => {
  if (!order.deliveryDate) return 'No delivery date';

  try {
    const date = new Date(order.deliveryDate);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return order.deliveryDate;
  }
};
